import * as Sentry from '@sentry/react';

import {sentryDns, releaseName, environment} from '~/shared/config';

import {remoteLoggingIgnoreList} from './consoleIgnoreList';

const NETWORK_ERROR = 'Network Error';

// standard ignoreErrors and denyUrls from https://gist.github.com/impressiver/5092952
const standardIgnores = {
  ignoreErrors: [
    // Random plugins/extensions
    'top.GLOBALS',
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    'Can\'t find variable: ZiteReader',
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    // Facebook borked
    'fb_xd_fragment',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
    // Generic error code from errors outside the security sandbox
    // You can delete this if using raven.js > 1.0, which ignores these automatically.
    'Script error.',
  ],
  denyUrls: [
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  ],
};

const denyUrls = [
  ...standardIgnores.denyUrls,
  /^https:\/\/dc\.services\.visualstudio\.com/,
  /^https:\/\/bam\..*\.nr-data\.net/,
  /^https:\/\/script\.crazyegg\.com/,
  /^https:\/\/stats\.g\.doubleclick\.net/,
  /^https:\/\/www\.google-analytics\.com/,
  /^https:\/\/.*\.appsflyer\.com/,
  /^https:\/\/.*\.hotjar\.io/,
  /^https:\/\/.*\.hotjar\.com/,
  /^https:\/\/ekr\.zdassets\.com/,
  /^https:\/\/.*\.intercom\.com/,
  /maps\.googleapis\.com/i,
  /chrome-extension:/i,
  /utag/,
];

const ignoreErrors = [...standardIgnores.ignoreErrors];

const sentryLogLevelMap = {
  verbose: 'debug',
  log: 'info',
  warn: 'warn',
  error: 'error',
  errorDialog: 'error',
};

export function logStructured({loggerName, logLevel, message, data}) {
  const level = sentryLogLevelMap[logLevel] || 'info';
  Sentry.logger[level](`[${loggerName}] ${message}`, data ? {data} : undefined);
}

export function init() {
  Sentry.init({
    dsn: sentryDns,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    release: releaseName,
    environment,
    tracesSampleRate: 0.05,
    _experiments: {
      enableLogs: true,
    },
    beforeBreadcrumb(event, hint) {
      const ignoreEvent =
        (hint?.xhr && denyUrls.some(i => i.test(event.data?.url || hint.xhr.responseURL))) ||
        (event?.message && remoteLoggingIgnoreList.some(i => i.test(event.message)));

      if (ignoreEvent) {
        return null;
      }

      return event;
    },
    ignoreErrors,
    denyUrls,
  });
}

export function log({loggerName, logLevel, errorOrMessage, args}) {
  Sentry.addBreadcrumb({
    category: loggerName,
    message: (errorOrMessage || '').toString(),
    level: logLevel,
    data: {
      args: JSON.stringify(args),
    },
  });
}

function shouldIgnoreError(args) {
  if (args?.[0]?.message?.includes(NETWORK_ERROR)) {
    return true;
  }
  // GetOrderSuccess returns a user-facing Hebrew error that is not actionable
  const isGetOrderSuccessError = ctx => ctx?.url?.includes('GetOrderSuccess') && ctx?.message?.includes('משהו השתבש');
  return isGetOrderSuccessError(args?.[0]) || isGetOrderSuccessError(args?.[0]?.e);
}

export function logError({error: rawError, loggerName, args}) {
  if (shouldIgnoreError(args)) {
    return;
  }

  log({errorOrMessage: rawError, loggerName, logLevel: 'error', args});
  Sentry.withScope(scope => {
    if ('fingerprint' in (args?.[0] || {})) {
      scope.setFingerprint(args[0].fingerprint);
    }
    Sentry.captureException(rawError);
  });
}
